import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Plus, Trash2, KeyRound, Check, XCircle, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang, imgUrl } from "@/i18n";

function Row({ name, sub, wa, testid, avatar, desc, onDelete, onReset, onEdit }) {
  const { t } = useLang();
  const { user } = useAuth();
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        {avatar ? (
          <img src={avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#165A2E]/10 font-bold text-[#165A2E]">{name?.[0]}</div>
        )}
        <div>
          <p className="font-semibold text-slate-800">{name}</p>
          <p className="text-xs text-slate-500">{sub}</p>
          {desc && <p className="mt-1 line-clamp-2 text-xs text-slate-400">{desc}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" data-testid={`${testid}-wa`}
            className="flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-1.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-[#1da851]">
            <MessageCircle size={13} /> {t("whatsapp")}
          </a>
        )}
        {onEdit && user?.role === "admin" && (
          <button onClick={onEdit} data-testid={`${testid}-edit`}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500 transition-colors duration-200 hover:bg-slate-600 hover:text-white"
            aria-label={t("edit")}>
            <Pencil size={14} />
          </button>
        )}
        {onReset && user?.role === "admin" && (
          <button onClick={onReset} data-testid={`${testid}-reset`}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#165A2E]/30 text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E] hover:text-white"
            aria-label={t("resetPassword")}>
            <KeyRound size={14} />
          </button>
        )}
        {onDelete && user?.role === "admin" && (
          <button onClick={onDelete} data-testid={`${testid}-delete`}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-red-200 text-red-500 transition-colors duration-200 hover:bg-red-500 hover:text-white"
            aria-label={t("delete")}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Directory() {
  const { t } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "staff" });
  const isStaff = ["admin", "staff"].includes(user.role);

  const load = async () => {
    const r = await api.get("/directory");
    setData(r.data);
    if (user.role === "admin") {
      const p = await api.get("/users", { params: { status: "pending" } });
      setPending(p.data);
    }
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success(t("saved"));
      setOpen(false);
      setForm({ name: "", email: "", password: "", phone: "", role: "staff" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [pending, setPending] = useState([]);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", role: "staff" });

  const approve = async (u) => {
    try {
      await api.post(`/users/${u.user_id}/approve`);
      toast.success(t("saved"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t("error")); }
  };

  const reject = async (u) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.post(`/users/${u.user_id}/reject`);
      toast.success(t("saved"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t("error")); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${editFor.user_id}`, editForm);
      toast.success(t("saved"));
      setEditFor(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t("error")); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/users/${resetFor.user_id}/reset-password`, { new_password: newPw });
      toast.success(t("passwordReset"));
      setResetFor(null);
      setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const delUser = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success(t("saved"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const delPlayer = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/players/${id}`);
      toast.success(t("saved"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  if (!data) return <p className="text-slate-500">{t("loading")}</p>;

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("directory")}</h1>
        {user.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn" className="rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">
                <Plus size={16} className="mr-1" /> {t("addUser")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("addUser")}</DialogTitle></DialogHeader>
              <form onSubmit={addUser} className="space-y-4">
                <div><Label>{t("name")}</Label><Input data-testid="user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
                <div><Label>{t("email")}</Label><Input data-testid="user-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
                <div><Label>{t("password")}</Label><Input data-testid="user-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
                <div><Label>{t("phone")}</Label><Input data-testid="user-phone" placeholder="0123456789" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
                <div>
                  <Label>{t("role")}</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="admin">Admin/Coach</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="user-submit-btn" className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">{t("save")}</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {pending.length > 0 && (
        <div className="gfa-card border-amber-300 bg-amber-50/50 p-5" data-testid="pending-section">
          <p className="mb-3 font-['Outfit'] font-bold text-amber-700">{t("pendingUsers")} ({pending.length})</p>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white p-4" data-testid={`pending-${u.user_id}`}>
                <div>
                  <p className="font-semibold text-slate-800">{u.name} <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">{u.registered_as || u.role}</span></p>
                  <p className="text-xs text-slate-500">{u.email}{u.phone ? ` · ${u.phone}` : ""}{u.ic_number ? ` · IC: ${u.ic_number}` : ""}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => approve(u)} data-testid={`approve-${u.user_id}`} className="rounded-full bg-[#165A2E] text-xs text-white hover:bg-[#114724]">
                    <Check size={13} className="mr-1" /> {t("approve")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject(u)} data-testid={`reject-${u.user_id}`} className="rounded-full border-red-300 text-xs text-red-600 hover:bg-red-50">
                    <XCircle size={13} className="mr-1" /> {t("reject")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="coaches">
        <TabsList>
          <TabsTrigger value="coaches" data-testid="tab-coaches">{t("coachesStaff")}</TabsTrigger>
          {isStaff && <TabsTrigger value="parents" data-testid="tab-parents">{t("parentsList")}</TabsTrigger>}
          {isStaff && <TabsTrigger value="players" data-testid="tab-players">{t("playersList")}</TabsTrigger>}
        </TabsList>
        <TabsContent value="coaches" className="space-y-3">
          {data.coaches_staff.map((u) => (
            <Row key={u.user_id} testid={`coach-${u.user_id}`} name={u.name}
              sub={`${u.role === "admin" ? "Admin/Coach" : "Staff"}${u.years_experience ? ` · ${u.years_experience} ${t("yearsShort")}` : ""} · ${u.phone_fmt || u.email}`}
              desc={u.experience || ""}
              avatar={u.picture ? imgUrl(u.picture) : ""}
              wa={u.phone_fmt ? u.wa : null}
              onDelete={u.user_id !== user.user_id ? () => delUser(u.user_id) : null}
              onReset={() => { setResetFor(u); setNewPw(""); }}
              onEdit={() => { setEditFor(u); setEditForm({ name: u.name, phone: u.phone || "", role: u.role }); }} />
          ))}
        </TabsContent>
        {isStaff && (
          <TabsContent value="parents" className="space-y-3">
            {(data.parents || []).map((u) => (
              <Row key={u.user_id} testid={`parent-${u.user_id}`} name={u.name} sub={u.phone_fmt || u.email} wa={u.phone_fmt ? u.wa : null} onDelete={() => delUser(u.user_id)} onReset={() => { setResetFor(u); setNewPw(""); }} onEdit={() => { setEditFor(u); setEditForm({ name: u.name, phone: u.phone || "", role: u.role }); }} />
            ))}
          </TabsContent>
        )}
        {isStaff && (
          <TabsContent value="players" className="space-y-3">
            {(data.players || []).map((p) => (
              <Row key={p.player_id} testid={`player-${p.player_id}`} name={p.name} sub={`${p.category} · ${p.parent_name} ${p.phone_fmt || ""}`} wa={p.phone_fmt ? p.wa : null}
                avatar={p.photo_path ? imgUrl(p.photo_path) : ""} onDelete={() => delPlayer(p.player_id)} />
            ))}
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!editFor} onOpenChange={(v) => !v && setEditFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("editUser")} — {editFor?.name}</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div>
              <Label>{t("name")}</Label>
              <Input data-testid="edit-user-name" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("phone")}</Label>
              <Input data-testid="edit-user-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("role")}</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin/Coach</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="parent">{t("asParent")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="edit-user-submit" className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">{t("save")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("resetPassword")} — {resetFor?.name}</DialogTitle></DialogHeader>
          <form onSubmit={doReset} className="space-y-4">
            <div>
              <Label>{t("newPassword")}</Label>
              <Input data-testid="reset-password-input" type="password" required minLength={6} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1" />
            </div>
            <Button data-testid="reset-password-submit" className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">{t("save")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
